<script>
    import { onMount } from "svelte";
    import { isAuthenticated, hostname, stack, showDebug } from "../stores.js";
    import { tooltip } from "svooltip";
    import 'svooltip/styles.css';
    import { getStorageQuotasApi, isNumeric } from '../utils.js';
    import { isSessionsListLoading } from "../pipelinestores.js";

    let DEFAULT_QUOTA_GB = 0; // Default quota in GB

    let EMPTY_STORAGE = {
        quota_GB: DEFAULT_QUOTA_GB, // The assigned allowed quota for the organisation
        used_quota_GB: 0, // Sum of tomographies_GB and reconstructions_GB
        tomographies_GB: 0, // Raw scan data
        reconstructions_GB: 0, // Volume resconstructions, both TIFFs and JPEGs
        previews_GB: 0, // Centershift and beamhard cupping previews
        intermediates_GB: 0 // Preprocessing, phase retrieval data
    };

    let usage = EMPTY_STORAGE;

    let totalUsage = 0;
    let percentageUsed = 0;

    let quotaLoaded = false;
    let quotaLoadedFailed = false;

    async function initialiseWithData() {
        try {
            quotaLoaded = false;
            quotaLoadedFailed = false;
            usage = EMPTY_STORAGE;
            const usageResponse = await getStorageQuotasApi($hostname, $isAuthenticated, $stack);
            if (isNumeric(usageResponse)) {
                quotaLoadedFailed = true;
            } else {
                usage = usageResponse?.organization_quota;
                quotaLoaded = true;
            }
            if (usage) {
                if (!isNumeric(usage.quota_GB)) {
                    usage.quota_GB = DEFAULT_QUOTA_GB;
                }
                calculateUsage();
            }
        } catch (error) {
            console.error("Error fetching storage quotas:", error);
            quotaLoadedFailed = true;
        }
    }

    onMount(async () => {
        calculateUsage();
    });

    let exceeding = false;
    function calculateUsage() {
        totalUsage = usage.used_quota_GB + usage.intermediates_GB + usage.previews_GB || 0;
        percentageUsed = (usage.used_quota_GB / usage.quota_GB) * 100 || 0;
        exceeding = percentageUsed > 100;
    }

    $: {
        if ($isAuthenticated) {
            console.log("~~~~~~~~~~~~~~~INITIALISING STORAGE WIDGET BECAUSE AUTHENTICATED~~~~~~~~~~~~~~~~");
            initialiseWithData();
        }
    }
    $: intermediateWidth = (usage.intermediates_GB / usage.quota_GB) * 100;
    $: previewWidth = (usage.previews_GB / usage.quota_GB) * 100;
    $: tomographiesWidth = (usage.tomographies_GB / usage.quota_GB) * 100;
    $: reconstructionsWidth = (usage.reconstructions_GB / usage.quota_GB) * 100;
    $: { 
        if($stack) {
            console.log("STORAGEWIDGET SEES stack: ", $stack);
            initialiseWithData();
        }
    }
</script>

<div class="storage-widget">
    <h3>Storage usage</h3>
    <div class="bar-container">
        <div class="bar-background" use:tooltip={{content: `Available storage: ${(usage.quota_GB - usage.used_quota_GB).toFixed(0)} GB`, placement: "right", delay: [300, 0]}}></div>
        <div class="bar-sections">
            {#if quotaLoaded}
                <div
                    class="bar-section tomographies {exceeding ? 'exceeding' : ''}"
                    style="width: {tomographiesWidth}%;"
                    use:tooltip={{content: `Raw scans storage: ${usage.tomographies_GB.toFixed(0)} GB              `, placement: "top", delay: [300, 0]}}>
                    Scans
                </div>
                <div
                    class="bar-section reconstructions {exceeding ? 'exceeding' : ''}"
                    style="width: {reconstructionsWidth}%;"
                    use:tooltip={{content: `Full reconstructions storage: ${usage.reconstructions_GB.toFixed(0)} GB`, placement: "top", delay: [300, 0]}}>
                    Reconstructions
                </div>
                {#if $showDebug}
                    <div
                        class="bar-section intermediate"
                        style="width: {intermediateWidth}%;"
                        use:tooltip={{content: `Intermediate storage: ${usage.intermediates_GB.toFixed(0)} GB (not counted against quota)`, placement: "top", delay: [300, 0]}}>
                        Intermediate
                    </div>
                    <div
                        class="bar-section preview"
                        style="width: {previewWidth}%;"
                        use:tooltip={{content: `Previews storage: ${usage.previews_GB.toFixed(0)} GB (not counted against quota)`, placement: "top", delay: [300, 0]}}>
                        Previews
                    </div>
                {/if}
            {:else}
                <div class="bar-section" style="color: var(--var-theme-base-color);">
                    {#if !quotaLoadedFailed}
                        Loading...
                    {:else}
                        Could not load storage usage info
                    {/if}
                </div>
            {/if}
        </div>
    </div>
    <div class="usage-info">
        <span style="display: inline;" 
            use:tooltip={{content: `${usage.used_quota_GB.toFixed(0)} GB`, placement: "top", delay: [300, 0]}}>
            Usage: {(usage.used_quota_GB / 1024).toFixed(0)} TB</span> / 
        <span use:tooltip={{content: `${usage.quota_GB.toFixed(0)} GB`, placement: "top", delay: [300, 0]}}>{(usage.quota_GB / 1024).toFixed(0)} TB</span>
        
        <span class="percentage-used" class:over-quota={percentageUsed > 100}>
            {percentageUsed.toFixed(0)}% used
            {#if exceeding}(exceeding quota){/if}
        </span>
        {#if $showDebug}
            <annotation style="display: block; margin-left: 0; margin-right: 0;">({totalUsage.toFixed(0)} GB including intermediate data and previews)</annotation>
        {/if}
    </div>
    <annotation style="display: block; margin-left: 0; margin-right: 0;">Total allowed quota is per agreement with Exciscope. Raw scan data (tomography data) and full reconstructions are counted towards this quota.
    </annotation>
</div>

<style>
    .storage-widget {
        font-family: Arial, sans-serif;
        max-width: 640px;
        margin: 8px 8px;
    }

    .bar-container {
        position: relative;
        height: 30px;
        background-color: #f0f0f0;
        border-radius: 2px;
        overflow: hidden;
    }

    .bar-background {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: #e0e0e0;
    }

    .bar-sections {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        display: flex;
        width: 100%;
        pointer-events: none;
    }

    .bar-section {
        height: 100%;
        pointer-events: all;
        font-size: small;
        color: whitesmoke;
        padding-left: 4px;
        padding-top: 8px;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .bar-section.tomographies {
        background-color: #054880;
    }

    .bar-section.tomographies.exceeding {
        background-color: #800505;
    }
    
    .bar-section.reconstructions {
        background-color: #21649c;
    }

    .bar-section.reconstructions.exceeding {
        background-color: #9c2121;
    }

    .bar-section.intermediate {
        background-color: #5a96c7;
    }

    .bar-section.preview {
        background-color: #788c9d;
    }

    .usage-info {
        display: block;
        margin-top: 8px;
        margin-left: 0;
        margin-bottom: 8px;
    }

    .percentage-used {
        display: inline;
        margin-left: 44px;
    }

    .over-quota {
        color: red;
        font-weight: bold;
    }
</style>
